import { DATE_TIME_FORMATS } from "constant";
import moment from "moment";

export const isSystem12HourFormat = () => {
    try {
        // Test with 1 PM (13:00) to detect format
        const testDate = new Date(2023, 0, 1, 13, 0, 0);
        const formatter = new Intl.DateTimeFormat(navigator.language, {
            hour: "numeric",
            hour12: undefined, // Let system decide
        });

        const formattedTime = formatter.format(testDate);

        // Extract the hour number from formatted string
        const hourMatch = formattedTime.match(/(\d+)/);
        const displayedHour = hourMatch ? parseInt(hourMatch[1]) : 0;

        // Simple logic: if 13:00 displays as "13", it's 24-hour format
        // if 13:00 displays as anything else (1, etc.), it's 12-hour format
        const is12Hour = displayedHour !== 13;

        return is12Hour;
    } catch (error) {
        // If anything fails, default to 12-hour format for better UX
        console.warn(
            "Error detecting time format, defaulting to 12-hour format:",
            error
        );
        return true;
    }
};

export const getDateTimeFormat = (includeSeconds = false) => {
    const is12Hour = isSystem12HourFormat();
    const { LONG_DT_12H, LONG_DT_24H, SHORT_DT_12H, SHORT_DT_24H } =
        DATE_TIME_FORMATS;

    if (includeSeconds) {
        return is12Hour ? LONG_DT_12H : LONG_DT_24H;
    }

    return is12Hour ? SHORT_DT_12H : SHORT_DT_24H;
};

export const getTimeFormat = (includeSeconds = false) => {
    const is12Hour = isSystem12HourFormat();
    const { LONG_TIME_12H, LONG_TIME_24H, SHORT_TIME_12H, SHORT_TIME_24H } =
        DATE_TIME_FORMATS;

    if (includeSeconds) {
        return is12Hour ? LONG_TIME_12H : LONG_TIME_24H;
    }

    return is12Hour ? SHORT_TIME_12H : SHORT_TIME_24H;
};

export const getInputTimeFormat = () => {
    const is12Hour = isSystem12HourFormat();
    const { INPUT_SHORT_TIME_12H, INPUT_SHORT_TIME_24H } = DATE_TIME_FORMATS;

    return is12Hour ? INPUT_SHORT_TIME_12H : INPUT_SHORT_TIME_24H;
};

const getFormattedTimeOfDay = (timeOfDay = "start", includeSeconds = false) => {
    let timeObj;

    if (timeOfDay === "end") {
        // For end of day, we use 23:59:59
        timeObj = moment().endOf("day");
    } else {
        // For start of day, we use 00:00:00
        timeObj = moment().startOf("day");
    }

    return timeObj.format(getTimeFormat(includeSeconds));
};

export const getStartTimeOfDay = (includeSeconds = false) => {
    return getFormattedTimeOfDay("start", includeSeconds);
};

export const getEndTimeOfDay = (includeSeconds = false) => {
    return getFormattedTimeOfDay("end", includeSeconds);
};

export const getCalendarFormat = () => {
    const timeFormat = getTimeFormat();
    const dtFormat = getDateTimeFormat();
    const calendarFormat = {
        sameDay: `[Today at] ${timeFormat}`,
        nextDay: `[Tomorrow at] ${timeFormat}`,
        nextWeek: `dddd [at] ${timeFormat}`,
        lastDay: `[Yesterday at] ${timeFormat}`,
        lastWeek: `[Last] dddd [at] ${timeFormat}`,
        sameElse: dtFormat,
    };

    return calendarFormat;
};

export const getValidDateTimeFormats = (is12Hour: boolean): string[] => {
    if (is12Hour) {
        return [
            "DD/MM/YYYY hh:mm A", // 27/05/2025 03:33 PM
            "DD/MM/YYYY h:mm A", // 27/05/2025 3:33 PM
        ];
    } else {
        return [
            "DD/MM/YYYY HH:mm", // 27/05/2025 15:33
            "DD/MM/YYYY H:mm", // 27/05/2025 3:33
        ];
    }
};
